import React, { useState, useEffect} from 'react';
import './App.css';
import PortalApp from "./PortalApp";
import {SnackbarProvider} from 'notistack';
import 'typeface-roboto';
import 'typeface-montserrat';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import PropTypes from "prop-types";
import {asyncForEach, getErrorMessageFromResponse,hasAccessToWorkspace} from "../common/helper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import {getTheme, prepareTheme} from "./helper";
import CustomSnackbar from "./CustomSnackbar";
import { ConfirmProvider } from "material-ui-confirm";
import {jwtDecode} from "jwt-decode";
import {useAuth0} from "@auth0/auth0-react";
import CryptoES from "crypto-es";
import randomBytes from "randombytes";

import { useCaseToken } from "../auth/auth0/CaseTokenProvider";

const HomeAuth0 = props => {

    const debug = window.location.pathname.toLowerCase().includes("debug");

    console.log ('HomeAuth0 props=', props)

    const { logout, user, getAccessTokenSilently } = useAuth0();

    //custom hook to get case token if required
    const {caseAccessToken, caseTokenExpiry, caseRefreshToken, caseTokenError} = useCaseToken();

    console.log ('HomeAuth0 user=', user)

    const audience = window.REACT_APP_AUTH0_AUDIENCE;

    debug && console.log ('HomeAuth0 user = ', user);

    const boxTokenRequired = window.REACT_APP_FOLDER_SOURCE === "box" ||
        window.REACT_APP_DOCUMENT_SOURCE === "box" ||
        window.REACT_APP_TASK_SOURCE === "box" ||
        window.REACT_APP_COMMENT_SOURCE === "box"

    const caseTokenRequired = window.REACT_APP_CASE_TOKEN_REQUIRED === 'true';
    const theme = getTheme()
    console.log ('theme=', theme)
    const portalThemeOptions = prepareTheme(theme);
    const portalTheme = createTheme((portalThemeOptions));
    const drawerWidth = theme.drawerWidth;

    const [loadingMsg, setLoadingMsg] = useState("Loading...")
    const [workspaceInfo, setWorkspaceInfo] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [errorDetails, setErrorDetails] = useState(null);
    const [initDone, setInitDone] = useState(false);
    const [configFiles, setConfigFiles] = useState({
        actionsConfig: {},
        dashboardsConfig: [],
        metadataConfig: {},
        optionsConfig: {},
        publicLinksConfig: [],
        templatesConfig: {},
        workflowConfig: {folderActions:[],documentActions:[]},
        workspaces: [],
        generalConfig:{}
    })

    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState("")
    const [snackbarSeverity, setSnackbarSeverity] = useState("info")

    function showSnackbar(message, messageSeverity)  {
        setSnackbarMessage(message)
        setSnackbarSeverity(messageSeverity)
        setOpenSnackbar(true)
    }

    function hideSnackbar()  {
        setSnackbarMessage("")
        setOpenSnackbar(false)
    }

    const auth0Domain = window.REACT_APP_AUTH0_DOMAIN;

    const redirectToGetCaseCode = () => {

        debug && console.log ('HomeAuth0 redirectToGetCaseCode')

        const state = CryptoES.SHA256(randomBytes(32).toString('hex')).toString(CryptoES.enc.Hex);
        const nonce = CryptoES.SHA256(randomBytes(32).toString('hex')).toString(CryptoES.enc.Hex);

        localStorage.setItem('state', state);
        localStorage.setItem('nonce', nonce);
        localStorage.setItem('originalPath', window.location.pathname + window.location.search); //for redirect from callback

        console.log ('originalPath = ', localStorage.getItem('originalPath'));

        const caseClientId = window.REACT_APP_AUTH0_CASE_CLIENT_ID;
        const redirectUri = `${window.location.origin}/callback`;
        const caseScope = window.REACT_APP_AUTH0_CASE_SCOPES;
        const auth0OrgId = window.REACT_APP_AUTH0_ORG_ID;

        let caseAuthURL =
            `https://${auth0Domain}/authorize?` +
            `client_id=${caseClientId}` +
            `&organization=${auth0OrgId}` +
            `&response_type=code` +
            `&redirect_uri=${encodeURIComponent(redirectUri)}` +
            `&scope=${encodeURIComponent(caseScope)}` +
            `&state=${state}` +
            `&nonce=${nonce}`;

        caseAuthURL += `&prompt=none`;
        console.log ('caseAuthURL=', caseAuthURL)

        window.open(caseAuthURL, "_self");
    }

    useEffect(() => {

        debug && console.log ('HomeAuth0 useEffect props=', props)

        window.localStorage.setItem('theme',"");

        async function init() {

            console.log ('HomeAuth0 init caseTokenRequired= ', caseTokenRequired, 'caseAccessToken= ', caseAccessToken)

            if (caseTokenRequired && !caseAccessToken ) {
                if (caseTokenError) {
                    console.error(caseTokenError)
                } else {
                    console.log('HomeAuth0 caseTokenRequired=true and caseAccessToken not set so redirect to /authorize endpoint')
                    redirectToGetCaseCode()
                }
            }

            //get config then set user info
            let config = await getConfigFiles();
            debug && console.log('init, got config files config=', config)

            const userRoles = user[audience + '/roles'] || [];
            const boxId = user[audience + '/boxId'] || "";
            debug && console.log('boxId=', boxId, 'userRoles=,', userRoles)
            debug && console.log('init, userRoles=', userRoles)

            const accessToken = await getAccessTokenSilently();
            const accessTokenDecoded = accessToken && jwtDecode(accessToken)
            debug && console.log ('accessTokenDecoded=', accessTokenDecoded)
            const accessTokenExpiry = accessTokenDecoded?.exp

            let boxAccessToken
            let boxAccessTokenExpiry

            if (boxTokenRequired) {

                const boxTokenResponse = await fetchBoxToken(accessToken);

                debug && console.log('boxTokenResponse=', boxTokenResponse)

                if (boxTokenResponse) {
                    boxAccessToken = boxTokenResponse.access_token || null;
                    boxAccessTokenExpiry = Math.floor(Date.now() / 1000) + Number(boxTokenResponse.expires_in);
                    debug && console.log('boxAccessToken=', boxAccessToken, 'boxAccessTokenExpiry=', boxAccessTokenExpiry)
                }
            }

            let workspaces = [];

            if (userRoles.length === 0) {
                setErrorDetails("No roles assigned");
            } else {
                //Loop through the Workspaces in config file, if the user has any of the accessroles listed for that workspace then add the Workspace to the list
                config.workspaces.forEach(ws => {
                    if (hasAccessToWorkspace(ws,userRoles)) {
                        workspaces.push(ws);
                    }
                })
                if (workspaces.length === 0) {
                    setErrorDetails("You are not configured to use this application");
                } else {
                    setWorkspaceInfo(workspaces);

                    setUserDetails(prev => ({
                        ...prev,
                        isUserLoggedIn: true,
                        userLoggedOut: false,
                        userName: user?.nickname,
                        userEmail: user?.email,
                        boxId: boxId,
                        userRoles: userRoles,
                        accessToken: accessToken,
                        accessTokenExpiry: accessTokenExpiry,
                        boxAccessToken: boxAccessToken,
                        boxAccessTokenExpiry: boxAccessTokenExpiry,
                        caseAccessToken: caseAccessToken,
                        caseTokenExpiry: caseTokenExpiry,
                        caseRefreshToken: caseRefreshToken,
                        otherInfo: "...."
                    }));
                }
            }

        }

        init().then (r=>{
            setInitDone(true)
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchBoxToken = async (accessToken) => {
        try {
            const url = window.REACT_APP_CONTENT_API_BASE_URL + window.REACT_APP_CONTENT_API_SECURITY_USER_TOKEN ;
            const request = { headers: { Authorization: "Bearer " + accessToken } };
            const response = await fetch(url, request);
            debug && console.log ('fetchBoxToken url=', url, 'request=', request, 'response=', response)
            if (!response.ok) {
                console.error('Failed to fetch box token.  Response=', response);
                const msg = await getErrorMessageFromResponse(response, 'Failed to fetch box token')
                console.error (msg)
                showSnackbar(msg, 'warning')
            } else {
                return await response.json();
            }

        } catch (err) {
            console.error('Failed to fetch box token:', err);
            showSnackbar(`Failed to fetch box token - ${err}`, 'warning')
        }
    };

    const refreshToken = async () => {

        const refreshCutoff = 600
        const now = Math.floor(Date.now() / 1000);

        let newAccessToken
        let newAccessTokenExpiry

        let newBoxToken
        let newBoxTokenExpiry

        let newCaseAccessToken
        let newCaseExpiry
        let newCaseRefreshToken


        debug && console.log ('refreshToken - refresh any tokens expiring in less than' ,refreshCutoff, 'seconds (', Math.floor(refreshCutoff/60),'minutes)')

        try {
            // 1. Refresh Auth0 Access Token
            debug && console.log("refreshToken - 1. Access Token");
            const accessTokenExpiry = userDetails.accessTokenExpiry;
            debug && console.log("refreshToken - accessTokenExpiry=", accessTokenExpiry);
            if (accessTokenExpiry) {
                const expiresInSeconds = parseInt(accessTokenExpiry, 10) - now;
                debug && console.log(`refreshToken - Auth0 access token expires in ${expiresInSeconds} seconds )`, Math.floor(expiresInSeconds/60),'minutes)');
                if (expiresInSeconds < refreshCutoff) {

                    newAccessToken =  await getAccessTokenSilently();
                    debug && console.log ('refreshedToken=', newAccessToken)

                    const accessTokenRefreshedDecoded = newAccessToken && jwtDecode(newAccessToken)
                    debug && console.log ('accessTokenRefreshedDecoded=', accessTokenRefreshedDecoded)
                    newAccessTokenExpiry = accessTokenRefreshedDecoded?.exp

                    const now = Math.floor(Date.now() / 1000);
                    const expiresInSeconds = parseInt(newAccessTokenExpiry, 10) - now;
                    debug && console.log(`Auth0 access token expires in ${expiresInSeconds} seconds (${Math.floor(expiresInSeconds/60)} minutes), (${Math.floor(expiresInSeconds/3600)} hours)`);

                } else {
                    console.log ('refreshToken - Auth0 token expiry > refreshCutoff so refresh not required')
                }
            }

            let boxAccessTokenExpiry = userDetails.boxAccessTokenExpiry


            //2. Refresh Box Token
            if (boxTokenRequired) {
                debug && console.log("refreshToken - 2. Box Token");
                debug && console.log("refreshToken - boxAccessTokenExpiry=", boxAccessTokenExpiry);
                if (boxAccessTokenExpiry) {
                    const boxExpiresInSeconds = parseInt(boxAccessTokenExpiry, 10) - now;
                    debug && console.log(`refreshToken - Box token expires in ${boxExpiresInSeconds} seconds (`, Math.floor(boxExpiresInSeconds/60),'minutes)');
                    if (boxExpiresInSeconds < refreshCutoff) {
                        //if access token was refreshed above then use that

                        const boxToken = await fetchBoxToken(newAccessToken ? newAccessToken : userDetails.accessToken);
                        newBoxToken = boxToken?.access_token;
                        newBoxTokenExpiry = now + Number(boxToken?.expires_in);
                        debug && console.log ('refreshToken newBoxExpiry = ', newBoxTokenExpiry)
                        debug && console.log ('refreshToken new boxAccessToken = ', newBoxToken)
                    } else {
                        console.log ('refreshToken - Box token expiry > refreshCutoff so refresh not required')
                    }
                }
            }

            // 3. Refresh Case Token
            if (caseTokenRequired) {
                debug && console.log("refreshToken - 3. Case Token");
                debug && console.log("refreshToken - caseTokenExpiry=", caseTokenExpiry);
                if (caseTokenExpiry) {
                    const caseExpiresInSeconds = caseTokenExpiry - now;
                    debug && console.log(`refreshToken - Case token expires in ${caseExpiresInSeconds} seconds (`,  Math.floor(caseExpiresInSeconds/60),'minutes)');
                    if (caseExpiresInSeconds < refreshCutoff) {
                        //if access token has been refreshed use that
                        const response = await refreshCaseToken(newAccessToken ? newAccessToken : userDetails.accessToken);
                        newCaseAccessToken = response?.access_token;
                        newCaseExpiry = now + Number(response?.expires_in);
                        debug && console.log("refreshToken - newCaseExpiry=", newCaseExpiry);
                        newCaseRefreshToken = response?.refresh_token;
                    }
                } else {
                    console.log ('refreshToken - Case token expiry > refreshCutoff so refresh not required')
                }
            }

            //All required refreshes done, now update userDetails
            const accessTokenChanged = !!(newAccessToken && newAccessToken !== userDetails.accessToken)
            const accessTokenExpiryChanged = !!(newAccessTokenExpiry && newAccessTokenExpiry !== userDetails.accessTokenExpiry)
            const boxTokenChanged =  !!(newBoxToken && newBoxToken !== userDetails.boxAccessToken)
            const boxTokenExpiryChanged = !!(newBoxTokenExpiry && newBoxTokenExpiry !== userDetails.boxAccessTokenExpiry)
            const caseTokenChanged = !!(newCaseAccessToken && newCaseAccessToken !== userDetails.caseAccessToken)
            const caseExpiryChanged =  !!(newCaseExpiry && newCaseExpiry !== userDetails.caseTokenExpiry)
            const caseRefreshTokenChanged =  !!(newCaseRefreshToken && newCaseRefreshToken !== userDetails.caseRefreshToken)


            if (debug) {
                console.log('refreshToken - accessToken=', userDetails.accessToken, '\n newAccessToken=', newAccessToken, '\n Changed?', accessTokenChanged)
                console.log('refreshToken - accessTokenExpiry', userDetails.accessTokenExpiry, '\n newAccessTokenExpiry=', newAccessTokenExpiry, '\n Changed?', accessTokenExpiryChanged)
                console.log('refreshToken - boxAccessToken', userDetails.boxAccessToken, '\n newBoxToken=', newBoxToken, '\n Changed?',boxTokenChanged)
                console.log('refreshToken - boxAccessTokenExpiry', userDetails.boxAccessTokenExpiry, '\n newBoxTokenExpiry=', newBoxTokenExpiry, '\n Changed?', boxTokenExpiryChanged)
                console.log('refreshToken - caseAccessToken', userDetails.caseAccessToken, '\n newCaseAccessToken=', newCaseAccessToken, '\n Changed?', caseTokenChanged)
                console.log('refreshToken - caseTokenExpiry', userDetails.caseTokenExpiry, '\n newCaseExpiry=', newCaseExpiry, '\n Changed?', caseExpiryChanged)
                console.log('refreshToken - caseRefreshToken', userDetails.caseRefreshToken, '\n newCaseRefreshToken', newCaseRefreshToken, '\n Changed?', caseRefreshTokenChanged);
            }

            if (accessTokenChanged || accessTokenExpiryChanged || boxTokenChanged || boxTokenExpiryChanged || caseTokenChanged || caseRefreshTokenChanged) {

            }

            setUserDetails(prev => ({
                ...prev,
                accessToken: newAccessToken ? newAccessToken : userDetails.accessToken,
                accessTokenExpiry: newAccessTokenExpiry ? newAccessTokenExpiry : userDetails.accessTokenExpiry,
                boxAccessToken: newBoxToken ? newBoxToken: userDetails.boxAccessToken,
                boxAccessTokenExpiry: newBoxTokenExpiry ? newBoxTokenExpiry : userDetails.boxAccessTokenExpiry,
                caseAccessToken: newCaseAccessToken ? newCaseAccessToken :  userDetails.caseAccessToken,
                caseTokenExpiry: newCaseExpiry ? newCaseExpiry : userDetails.caseTokenExpiry,
                caseRefreshToken: newCaseRefreshToken ? newCaseRefreshToken : userDetails.caseRefreshToken
            }))

            debug && console.log('refreshToken done');

        } catch (err) {
            console.error("Error during token refresh sequence:", err);
        }
    };

    const refreshCaseToken = async (accessToken) => {
        try {
            const url = window.window.REACT_APP_SECURITY_API_BASE_URL + "/elastic/refresh"
            const body = {"refresh_token": userDetails.caseRefreshToken};
            const request = {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + accessToken
                },
                body: JSON.stringify(body)
            };

            const response = await fetch(url, request);
            debug && console.log ('refreshCaseToken url=', url, 'request=', request, 'response=', response)
            if (!response.ok) {
                console.error('Failed to refresh case token.  Response=', response);
                let msg = ""
                if (response.status === 401) {
                    msg = "Session expired. Please return to the login screen and log in again.";
                    showSnackbar(msg, 'warning')
                } else {
                    msg = getErrorMessageFromResponse(response, 'Failed to refresh case token')
                    showSnackbar(msg, 'warning')
                }

            } else {
                return await response.json();
            }

        } catch (err) {
            console.error('Failed to refresh case token:', err);
            showSnackbar(`${err}`, 'warning')
        }
    };

    async function getConfigFiles() {

        const accessToken = await getAccessTokenSilently();

        let config = {
            actionsConfig: {},
            dashboardsConfig: [],
            metadataConfig: {},
            optionsConfig: {},
            publicLinksConfig: [],
            templatesConfig: {},
            workflowConfig: {folderActions:[],documentActions:[]},
            workspaces: [],
        };

        // application config - either retrieve from api or pull from local config
        if (window.REACT_APP_CONFIG_API_REQUIRED === "true" || window.REACT_APP_CONFIG_API_REQUIRED === true) {
            const url = window.REACT_APP_CONFIG_API_BASE_URL;
            const request = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + accessToken
                }
            };
            debug && console.log('get config files from service..url: ', url, 'request:', request);

            fetch(url, request)
                .then(response => response.json())
                .then(data => {
                    debug && console.log('getConfigFiles - response.json', data);

                    config = {
                        workspaces: JSON.parse(data.workspace),
                        actionsConfig: JSON.parse(data.action),
                        metadataConfig: JSON.parse(data.metadata),
                        optionsConfig: JSON.parse(data.metadataoption),
                        publicLinksConfig: JSON.parse(data.link),
                        dashboardsConfig: JSON.parse(data.link),
                    };

                })
                .catch(e => {
                    console.log(e);
                    setErrorDetails("Config Retrieval Error" + e);
                })
        } else {

            debug && console.log ('get config from local config')

            const configFilePaths = {
                workspaces: 'config/configWorkspaces.json',
                actionsConfig: 'config/configActions.json',
                metadataConfig: 'config/configMetadata.json',
                optionsConfig: 'config/configMetadataOptions.json',
                publicLinksConfig: 'config/configPublicLinks.json',
                templatesConfig: 'config/configTemplates.json',
                workflowConfig: 'config/configWorkflow.json',
                dashboardsConfig: 'config/configDashboards.json',
                generalConfig: 'config/configGeneral.json'
            };

            const configPrefix =
                window.location.href.indexOf("file") > -1 || window.location.href.indexOf("folder") > -1 || window.location.href.indexOf("upload") > -1 ? "../../" :
                    window.location.href.indexOf("dashboard") > -1 ? "../" :
                        "";

            debug && console.log ('configPrefix=', configPrefix);

            await asyncForEach(Object.entries(configFilePaths), async (entry) => {

                let url = configPrefix + entry[1];

                await fetch(url)
                    .then(response => {
                        return(response.json())
                    })
                    .then(data => {
                        config[entry[0]] =  data;
                    })
                    .catch(e => {
                        debug && console.log("getConfigFiles exception:" , e, " entry: ", entry);
                    })
            })
        }
        debug && console.log('getConfigFiles done, config =  ', config);
        setConfigFiles(config);
        return config
    }

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={portalTheme}>
                <ConfirmProvider>
                    <div>
                        {/*enqueueSnackbar not available here so use custom snackbar*/}
                        <CustomSnackbar message={snackbarMessage} open={openSnackbar} severity={snackbarSeverity} handleClose={hideSnackbar}/>
                        <SnackbarProvider maxSnack={1} style={{maxWidth: 800}}
                                          iconVariant={{error: <i className="material-icons" style={{paddingRight: '10px'}}>error</i>}}>
                            {
                                errorDetails ?
                                    <div style={{textAlign: "center", borderColor: '#ddd #ddd #d8d8d8', boxShadow: 'box-shadow: 0 2px 0 hsla(0,0%,68.6%,.12', width: "50%", height: "50%", overflow: 'visible', margin: 'auto', position: 'absolute', top: '0', left: '0', bottom: '0', right: '0'}}>
                                        <img src={window.location.origin + '/images/authlogo.png'} alt="logo"
                                             style={{display: "block", margin: "0 auto", maxWidth: "200px", maxHeight: "40px"}}/><br/><br/>
                                        <Typography variant="h6">{window.REACT_APP_APP_TITLE}</Typography><br/><br/>
                                        <Typography variant="subtitle1">An error has occurred, please retry.</Typography><br/><br/>
                                        <Typography variant="subtitle2">{errorDetails}</Typography><br/><br/>
                                        <Button variant="contained" color="secondary" onClick={() => logout({ returnTo: window.location.origin })} style={{width: "200px", marginTop: "20px"}}>Return to login</Button>
                                    </div>
                                :
                                initDone && userDetails && workspaceInfo ?
                                    <PortalApp
                                        {...props}
                                        userDetails={userDetails}
                                        logout={logout}
                                        triggerRefreshAuthToken = {refreshToken}
                                        configFiles={configFiles}
                                        workspaceConfig={workspaceInfo}
                                        launchDashboard={props.launchDashboard}
                                        drawerWidth={drawerWidth}
                                    /> :
                                    <React.Fragment>
                                        {/*<LinearProgress style={{backgroundColor: '#808080'}}/>*/}
                                        <LinearProgress color="secondary"/>
                                        <div style={{color: '#808080', fontSize: '0.75rem', fontWeight: '500', lineHeight: '24px', paddingLeft: '6px'}}>
                                            {loadingMsg}
                                        </div>
                                    </React.Fragment>
                            }
                        </SnackbarProvider>
                    </div>
                </ConfirmProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

HomeAuth0.propTypes = {
    user: PropTypes.object.isRequired,
    logout: PropTypes.func.isRequired,
    // refreshToken: PropTypes.func.isRequired,
    launchDashboard: PropTypes.bool.isRequired,
    //lock: PropTypes.object.isRequired

};
export default HomeAuth0;